import { useEffect, useMemo, useState } from "react";
import { Loader2, Eye, AlertTriangle, RefreshCw, Upload, EyeOff, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { acpProducts, acpPublish, acpPreview, acpSync } from "@/lib/api";
import { StatusPill, fmtDate, money } from "./acpUi";

const Flag = ({ on, testId }) => (on ? <Check size={14} className="text-emerald-600 inline" data-testid={testId} /> : <X size={14} className="text-zinc-300 inline" data-testid={testId} />);

export function AcpProducts({ caller }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [modal, setModal] = useState(null); // {title, item, errors}
  const load = () => acpProducts(caller).then((r) => setRows(r.products || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, [caller]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (rows || []).filter((r) => !n || r.name.toLowerCase().includes(n) || String(r.sku).includes(n));
  }, [rows, q]);

  async function act(kind, r) {
    setBusy(r.id + kind);
    try {
      if (kind === "publish" || kind === "unpublish") {
        await acpPublish(caller, r.id, kind === "publish");
        toast.success(`${r.name} ${kind === "publish" ? "published to" : "removed from"} ChatGPT`);
        await load();
      } else if (kind === "sync") {
        const s = await acpSync(caller);
        toast.success(`Feed synced · ${s.count} items`);
        await load();
      } else if (kind === "preview") {
        const p = await acpPreview(caller, r.id);
        setModal({ title: `ACP data · ${r.name}`, item: p.item, errors: p.errors });
      } else if (kind === "errors") {
        setModal({ title: `Errors · ${r.name}`, errors: r.errors });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy("");
    }
  }

  if (!rows) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>;
  const btn = "h-7 px-2 rounded-md border border-zinc-200 text-[11px] font-semibold inline-flex items-center gap-1 hover:bg-zinc-50 disabled:opacity-50";
  return (
    <div className="space-y-3" data-testid="acp-products">
      <div className="flex items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or SKU…" className="h-9 rounded-xl max-w-xs" data-testid="acp-products-search" />
        <span className="text-xs text-zinc-500">{list.length} products</span>
      </div>
      <div className="space-y-2" data-testid="acp-products-list">
        {list.map((r) => (
          <div key={r.id} className="rounded-2xl bg-white border border-zinc-200 p-3" data-testid={`acp-product-row-${r.id}`}>
            <div className="flex items-start gap-3">
              {r.image_url ? <img src={r.image_url} alt="" className="h-12 w-12 rounded-lg object-cover bg-zinc-100 shrink-0" /> : <div className="h-12 w-12 rounded-lg bg-zinc-100 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium text-zinc-900 truncate">{r.name}</div>
                  <StatusPill status={r.status} testId={`acp-status-${r.id}`} />
                </div>
                <div className="text-xs text-zinc-500 mt-0.5 flex flex-wrap gap-x-3">
                  <span>SKU <span className="font-mono text-zinc-700">{r.sku}</span></span>
                  <span>{money(r.price)}</span>
                  <span className={r.inventory > 0 ? "" : "text-rose-600"}>Inventory {r.inventory}</span>
                  <span>Synced {fmtDate(r.last_sync)}</span>
                </div>
                <div className="text-[11px] text-zinc-600 mt-1 flex flex-wrap gap-x-3">
                  <span>ACP eligible <Flag on={r.acp_eligible} /></span>
                  <span>Published <Flag on={r.published} testId={`acp-published-${r.id}`} /></span>
                  <span>Instant checkout <Flag on={r.instant_checkout_eligible} /></span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-wrap mt-2">
              {r.published ? (
                <button type="button" className={btn} disabled={!!busy} onClick={() => act("unpublish", r)} data-testid={`acp-unpublish-${r.id}`}><EyeOff size={12} /> Unpublish</button>
              ) : (
                <button type="button" className={`${btn} bg-zinc-900 text-white border-zinc-900 hover:bg-zinc-800`} disabled={!!busy || !r.acp_eligible} onClick={() => act("publish", r)} data-testid={`acp-publish-${r.id}`}><Upload size={12} /> Publish</button>
              )}
              <button type="button" className={btn} disabled={!!busy} onClick={() => act("sync", r)} data-testid={`acp-sync-${r.id}`}>{busy === r.id + "sync" ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Sync</button>
              <button type="button" className={btn} onClick={() => act("preview", r)} data-testid={`acp-preview-${r.id}`}><Eye size={12} /> Preview ACP data</button>
              {r.errors.length > 0 && <button type="button" className={`${btn} text-rose-700 border-rose-200`} onClick={() => act("errors", r)} data-testid={`acp-errors-${r.id}`}><AlertTriangle size={12} /> View errors ({r.errors.length})</button>}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="acp-preview-dialog">
          <DialogHeader><DialogTitle>{modal?.title}</DialogTitle><DialogDescription>Exactly what ChatGPT receives for this product.</DialogDescription></DialogHeader>
          {modal?.errors?.length > 0 && (
            <ul className="text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded-lg p-3 space-y-1" data-testid="acp-error-list">
              {modal.errors.map((e) => <li key={e}>• {e}</li>)}
            </ul>
          )}
          {modal?.item && <pre className="text-[11px] bg-zinc-950 text-zinc-100 rounded-xl p-3 overflow-auto max-h-[50vh]" data-testid="acp-preview-json">{JSON.stringify(modal.item, null, 2)}</pre>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
